/* AIModeToggle — Sélecteur unifié Interne · Web · Hybride
 *
 * Composant réutilisable injecté dans tous les modules IA. Trois modes :
 *   🧠 Interne — NOAM sur les données Noam (mode historique)
 *   🌐 Web     — Tavily uniquement (recherche externe)
 *   ⚡ Hybride — NOAM croise interne + web (mode Palantir)
 *
 * Si Tavily/Perplexity ne sont pas configurés, les modes Web et Hybride
 * sont visuellement grisés et un tooltip explique. Le mode Interne
 * reste toujours disponible. Le composant gère automatiquement le
 * fallback graceful (jamais d'erreur bloquante).
 *
 * Props :
 *   value     : "internal" | "web" | "hybrid"
 *   onChange  : (newMode) => void
 *   compact   : variante mini (juste 3 icônes)
 *   className : classe additionnelle
 *
 * Esthétique : intégrée au design system Noam (bordures subtiles, accents
 * couleur identité, hover transitions). Aucun effet criard.
 */
import { useEffect, useState } from "react";
import { Brain, Globe, Zap, Lock } from "lucide-react";
import { api } from "@/lib/api";

const MODES = [
  { key: "internal", label: "Interne", icon: Brain,  desc: "NOAM sur vos données Noam",      color: "#378ADD" },
  { key: "web",      label: "Web",     icon: Globe,  desc: "Tavily — recherche web temps réel", color: "#1D9E75" },
  { key: "hybrid",   label: "Hybride", icon: Zap,    desc: "Croise interne + web (Palantir)",  color: "#EF9F27" },
];

let _CACHED_AVAILABILITY = null;
let _CACHE_TS = 0;

async function fetchAvailability() {
  if (_CACHED_AVAILABILITY && Date.now() - _CACHE_TS < 60000) {
    return _CACHED_AVAILABILITY;
  }
  try {
    const r = await api.get("/workspace/hybrid/availability");
    _CACHED_AVAILABILITY = r.data;
    _CACHE_TS = Date.now();
    return r.data;
  } catch {
    return { internal: true, web: false, hybrid: false };
  }
}

export default function AIModeToggle({ value = "internal", onChange, compact = false, className = "" }) {
  const [avail, setAvail] = useState({ internal: true, web: true, hybrid: true });

  useEffect(() => {
    fetchAvailability().then(setAvail);
  }, []);

  return (
    <div
      className={`inline-flex items-center gap-0.5 p-0.5 rounded-md border border-white/10 bg-white/[0.02] ${className}`}
      data-testid="ai-mode-toggle"
    >
      {MODES.map((m) => {
        const Icon = m.icon;
        const isAvail = avail[m.key] !== false;
        const isActive = value === m.key;
        return (
          <button
            key={m.key}
            type="button"
            onClick={() => isAvail && onChange?.(m.key)}
            disabled={!isAvail}
            className={`relative inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] uppercase tracking-wider font-semibold transition-all ${
              isActive
                ? "bg-white/10 text-white"
                : isAvail
                  ? "text-zinc-500 hover:text-zinc-200"
                  : "text-zinc-700 cursor-not-allowed"
            }`}
            style={isActive ? { boxShadow: `inset 0 0 0 1px ${m.color}40`, color: m.color } : undefined}
            data-testid={`ai-mode-${m.key}`}
            title={isAvail ? m.desc : `${m.desc} — clé non configurée`}
          >
            {!isAvail && <Lock className="w-2.5 h-2.5 absolute -top-0.5 -right-0.5 text-zinc-700" />}
            <Icon className="w-3 h-3" style={isActive ? { color: m.color } : undefined} />
            {!compact && <span>{m.label}</span>}
          </button>
        );
      })}
    </div>
  );
}

// Helper export pour partager le mode dans tout le composant parent
export function useAIMode(initial = "internal") {
  const [mode, setMode] = useState(initial);
  return [mode, setMode];
}
